import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ownersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

function Owners() {
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pages: 1, total: 0 });
  const { hasPermission } = useAuth();

  useEffect(() => {
    loadOwners();
  }, [search, typeFilter, pagination.current]);

  const loadOwners = async () => {
    setLoading(true);
    try {
      const response = await ownersAPI.getAll({
        search,
        type: typeFilter,
        page: pagination.current
      });
      setOwners(response.data.owners);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('Erro ao carregar proprietários');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Deseja realmente excluir o proprietário "${name}"?`)) {
      return;
    }

    try {
      await ownersAPI.delete(id);
      toast.success('Proprietário excluído com sucesso');
      loadOwners();
    } catch (error) {
      const message = error.response?.data?.message || 'Erro ao excluir proprietário';
      toast.error(message);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, current: 1 }));
    loadOwners();
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Proprietários</h1>
          <p className="page-subtitle">Gerencie os proprietários de imóveis cadastrados</p>
        </div>
        <Link to="/proprietarios/novo" className="btn btn-primary">
          ➕ Novo Proprietário
        </Link>
      </div>

      {/* Filters */}
      <form onSubmit={handleSearch} className="filters-bar">
        <input
          type="text"
          className="form-input filter-input"
          placeholder="Buscar por nome, e-mail, CPF ou CNPJ..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="form-select filter-select"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="">Todos os tipos</option>
          <option value="Pessoa Física">Pessoa Física</option>
          <option value="Pessoa Jurídica">Pessoa Jurídica</option>
        </select>
        <button type="submit" className="btn btn-secondary">
          🔍 Buscar
        </button>
      </form>

      {/* Table */}
      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? (
            <div className="loading-spinner">
              <div className="spinner"></div>
            </div>
          ) : owners.length > 0 ? (
            <>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Tipo</th>
                      <th>CPF/CNPJ</th>
                      <th>E-mail</th>
                      <th>Telefone</th>
                      <th>Cidade/UF</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {owners.map((owner) => (
                      <tr key={owner._id}>
                        <td>
                          <strong>{owner.name}</strong>
                        </td>
                        <td>
                          <span className={`badge ${owner.type === 'Pessoa Física' ? 'badge-info' : 'badge-primary'}`}>
                            {owner.type}
                          </span>
                        </td>
                        <td>{owner.cpf || owner.cnpj || '-'}</td>
                        <td>{owner.email}</td>
                        <td>{owner.phone}</td>
                        <td>
                          {owner.address?.city && owner.address?.state 
                            ? `${owner.address.city}/${owner.address.state}`
                            : '-'
                          }
                        </td>
                        <td>
                          <div className="table-actions">
                            <Link 
                              to={`/proprietarios/${owner._id}/editar`}
                              className="btn btn-sm btn-outline"
                              title="Editar"
                            >
                              ✏️
                            </Link>
                            {hasPermission('Administrador', 'Analista') && (
                              <button
                                onClick={() => handleDelete(owner._id, owner.name)}
                                className="btn btn-sm btn-danger"
                                title="Excluir"
                              >
                                🗑️
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="pagination">
                  <button
                    className="pagination-btn"
                    disabled={pagination.current === 1}
                    onClick={() => setPagination(prev => ({ ...prev, current: prev.current - 1 }))}
                  >
                    ◀
                  </button>
                  {[...Array(pagination.pages)].map((_, i) => (
                    <button
                      key={i + 1}
                      className={`pagination-btn ${pagination.current === i + 1 ? 'active' : ''}`}
                      onClick={() => setPagination(prev => ({ ...prev, current: i + 1 }))}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    className="pagination-btn"
                    disabled={pagination.current === pagination.pages}
                    onClick={() => setPagination(prev => ({ ...prev, current: prev.current + 1 }))}
                  >
                    ▶
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">👤</div>
              <div className="empty-state-title">Nenhum proprietário encontrado</div>
              <div className="empty-state-text">
                {search || typeFilter 
                  ? 'Tente ajustar os filtros de busca'
                  : 'Cadastre o primeiro proprietário para começar'
                }
              </div>
              {!search && !typeFilter && (
                <Link to="/proprietarios/novo" className="btn btn-primary" style={{ marginTop: '16px' }}>
                  ➕ Cadastrar Proprietário
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Owners;
